import JsonModels from '@/ts/models.json';

/**
 * Finds the part of a value a quick search matched (SAU-467), so it can be highlighted.
 *
 * It matches the way the backend searches: case-insensitive, with each plain letter also
 * matching its accented forms, so "cafe" highlights "Café". The accent table comes from
 * models.json `_constants`, which `make:ts-interfaces` writes from the backend's own
 * table, so the highlight and the search cannot disagree about a letter.
 */

/** A matched part of a string: [start, end) in UTF-16 code units, as JavaScript counts. */
export type MatchRange = [number, number];

const ACCENT_VARIANTS: Record<string, string> =
    ((JsonModels as unknown as { _constants?: { QUICK_SEARCH_ACCENT_VARIANTS?: Record<string, string> } })._constants
        ?.QUICK_SEARCH_ACCENT_VARIANTS) ?? {};

/**
 * The typed text as the backend splits it, the same in Matches all and Matches any. Each
 * word is a term, and a part in double quotes is one term with its spaces collapsed. A
 * quote with no partner is dropped, so its words are terms of their own. A term typed
 * twice, in any case, is kept once.
 */
export function quickSearchTerms(text: string): string[] {
    let unpaired = text;
    if ((text.match(/"/gu) ?? []).length % 2 === 1) {
        const lastQuote = text.lastIndexOf('"');
        unpaired = `${text.slice(0, lastQuote)} ${text.slice(lastQuote + 1)}`;
    }

    const terms = new Map<string, string>();
    for (const match of unpaired.matchAll(/"([^"]*)"|([^\s"]+)/gu)) {
        const term = (match[2] ?? match[1]).replace(/\s+/gu, ' ').trim();
        if (term !== '' && !terms.has(term.toLowerCase())) {
            terms.set(term.toLowerCase(), term);
        }
    }
    return [...terms.values()];
}

/** Escapes the characters a Unicode-mode RegExp treats specially. Any other escape is an error there. */
function escapeForRegExp(character: string): string {
    return character.replace(/[.*+?^${}()|[\]\\/]/gu, '\\$&');
}

function termPattern(term: string): RegExp {
    const body = Array.from(term.toLowerCase())
        .map((character) => (ACCENT_VARIANTS[character] ? `[${ACCENT_VARIANTS[character]}]` : escapeForRegExp(character)))
        .join('');
    return new RegExp(body, 'giu');
}

/** Every part of the text any term matches, merged where they touch, in order. */
export function matchRanges(text: string, terms: string[]): MatchRange[] {
    const ranges: MatchRange[] = [];
    for (const term of terms) {
        if (term === '') continue;
        for (const match of text.matchAll(termPattern(term))) {
            if (match[0].length > 0 && match.index !== undefined) {
                ranges.push([match.index, match.index + match[0].length]);
            }
        }
    }
    ranges.sort((first, second) => first[0] - second[0]);
    const merged: MatchRange[] = [];
    for (const range of ranges) {
        const last = merged[merged.length - 1];
        if (last && range[0] <= last[1]) {
            last[1] = Math.max(last[1], range[1]);
        } else {
            merged.push([range[0], range[1]]);
        }
    }
    return merged;
}

/**
 * The DOM ranges for matched parts of an element's text, across however many text nodes
 * the element's rendering splits it into. Nothing in the DOM is changed.
 */
export function domRangesFor(element: Element, textRanges: MatchRange[]): Range[] {
    if (textRanges.length === 0) {
        return [];
    }
    const walker = element.ownerDocument.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const nodes: { node: Text; start: number }[] = [];
    let offset = 0;
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const textNode = node as Text;
        nodes.push({ node: textNode, start: offset });
        offset += textNode.data.length;
    }

    const locate = (position: number, isEnd: boolean): { node: Text; offset: number } | null => {
        for (const entry of nodes) {
            const end = entry.start + entry.node.data.length;
            if (position < end || (isEnd && position === end)) {
                return { node: entry.node, offset: position - entry.start };
            }
        }
        return null;
    };

    const domRanges: Range[] = [];
    for (const [start, end] of textRanges) {
        const from = locate(start, false);
        const to = locate(end, true);
        if (!from || !to) continue;
        const range = element.ownerDocument.createRange();
        range.setStart(from.node, from.offset);
        range.setEnd(to.node, to.offset);
        domRanges.push(range);
    }
    return domRanges;
}

/**
 * The browser's highlight registry, one named highlight per surface, so the list and
 * Cmd+K each own their marks. A browser without the CSS Custom Highlight API simply
 * shows no marks; the results themselves are unchanged.
 */
const rangesByOwner = new Map<string, Map<object, Range[]>>();

interface HighlightRegistry {
    set(name: string, highlight: unknown): void;
    delete(name: string): void;
}

function highlightRegistry(): { registry: HighlightRegistry; create: (ranges: Range[]) => unknown } | null {
    const registry = (globalThis.CSS as unknown as { highlights?: HighlightRegistry } | undefined)?.highlights;
    const HighlightClass = (globalThis as unknown as { Highlight?: new (...ranges: Range[]) => unknown }).Highlight;
    return registry && HighlightClass ? { registry, create: (ranges) => new HighlightClass(...ranges) } : null;
}

/** Sets one owner's ranges in a named highlight, replacing what that owner had. */
export function setHighlightRanges(name: string, owner: object, ranges: Range[]): void {
    const owners = rangesByOwner.get(name) ?? new Map<object, Range[]>();
    rangesByOwner.set(name, owners);
    // Most cells of a list match nothing. Clearing what an owner never had changes
    // nothing, so it skips rebuilding the highlight.
    if (ranges.length === 0 && !owners.has(owner)) {
        return;
    }
    if (ranges.length > 0) {
        owners.set(owner, ranges);
    } else {
        owners.delete(owner);
    }

    const browser = highlightRegistry();
    if (!browser) return;
    const all = [...owners.values()].flat();
    if (all.length === 0) {
        browser.registry.delete(name);
    } else {
        browser.registry.set(name, browser.create(all));
    }
}

/** The names the app styles with `::highlight(...)` in app.css. */
export const LIST_HIGHLIGHT = 'quick-search-list';
export const PALETTE_HIGHLIGHT = 'quick-search-palette';
